import { z } from "zod";
import {
  ActionSetDtoSchema,
  LibraryUsageDtoSchema,
  MatchSetDtoSchema,
  type ActionSetDto,
  type LibraryUsageDto,
  type MatchSetDto,
  type NewActionSetDto,
  type NewMatchSetDto,
  type UpdateActionSetDto,
  type UpdateMatchSetDto,
} from "./types";
import { API_BASE, handleResponse, parseResponse } from "./common";
import { fetchWithInterceptor } from "./client";

const MATCH = `${API_BASE}/settings/library/match-sets`;
const ACTION = `${API_BASE}/settings/library/action-sets`;

const json = (method: string, body: unknown) => ({
  method,
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify(body),
});

export const getMatchSets = async (): Promise<MatchSetDto[]> => {
  const res = await fetchWithInterceptor(MATCH);
  await handleResponse(res);
  return parseResponse(
    z.array(MatchSetDtoSchema),
    await res.json(),
    "GET /settings/library/match-sets",
  );
};

export const getMatchSet = async (id: string): Promise<MatchSetDto> => {
  const res = await fetchWithInterceptor(`${MATCH}/${id}`);
  await handleResponse(res);
  return parseResponse(
    MatchSetDtoSchema,
    await res.json(),
    "GET /settings/library/match-sets/:id",
  );
};

export const createMatchSet = async (
  set: NewMatchSetDto,
): Promise<MatchSetDto> => {
  const res = await fetchWithInterceptor(MATCH, json("POST", set));
  await handleResponse(res);
  return parseResponse(
    MatchSetDtoSchema,
    await res.json(),
    "POST /settings/library/match-sets",
  );
};

export const updateMatchSet = async (
  id: string,
  set: UpdateMatchSetDto,
): Promise<MatchSetDto> => {
  const res = await fetchWithInterceptor(`${MATCH}/${id}`, json("PUT", set));
  await handleResponse(res);
  return parseResponse(
    MatchSetDtoSchema,
    await res.json(),
    "PUT /settings/library/match-sets/:id",
  );
};

export const deleteMatchSet = async (id: string): Promise<void> => {
  const res = await fetchWithInterceptor(`${MATCH}/${id}`, {
    method: "DELETE",
  });
  await handleResponse(res);
};

/**
 * Which rules and other library entries cite this set. The delete guard rejects
 * a delete while this is non-empty, so the UI fetches it to say what is in the
 * way rather than just refusing.
 */
export const getMatchSetUsage = async (
  id: string,
): Promise<LibraryUsageDto> => {
  const res = await fetchWithInterceptor(`${MATCH}/${id}/usage`);
  await handleResponse(res);
  return parseResponse(
    LibraryUsageDtoSchema,
    await res.json(),
    "GET /settings/library/match-sets/:id/usage",
  );
};

export const getActionSets = async (): Promise<ActionSetDto[]> => {
  const res = await fetchWithInterceptor(ACTION);
  await handleResponse(res);
  return parseResponse(
    z.array(ActionSetDtoSchema),
    await res.json(),
    "GET /settings/library/action-sets",
  );
};

export const getActionSet = async (id: string): Promise<ActionSetDto> => {
  const res = await fetchWithInterceptor(`${ACTION}/${id}`);
  await handleResponse(res);
  return parseResponse(
    ActionSetDtoSchema,
    await res.json(),
    "GET /settings/library/action-sets/:id",
  );
};

export const createActionSet = async (
  set: NewActionSetDto,
): Promise<ActionSetDto> => {
  const res = await fetchWithInterceptor(ACTION, json("POST", set));
  await handleResponse(res);
  return parseResponse(
    ActionSetDtoSchema,
    await res.json(),
    "POST /settings/library/action-sets",
  );
};

export const updateActionSet = async (
  id: string,
  set: UpdateActionSetDto,
): Promise<ActionSetDto> => {
  const res = await fetchWithInterceptor(`${ACTION}/${id}`, json("PUT", set));
  await handleResponse(res);
  return parseResponse(
    ActionSetDtoSchema,
    await res.json(),
    "PUT /settings/library/action-sets/:id",
  );
};

export const deleteActionSet = async (id: string): Promise<void> => {
  const res = await fetchWithInterceptor(`${ACTION}/${id}`, {
    method: "DELETE",
  });
  await handleResponse(res);
};

export const getActionSetUsage = async (
  id: string,
): Promise<LibraryUsageDto> => {
  const res = await fetchWithInterceptor(`${ACTION}/${id}/usage`);
  await handleResponse(res);
  return parseResponse(
    LibraryUsageDtoSchema,
    await res.json(),
    "GET /settings/library/action-sets/:id/usage",
  );
};
